/**
 * Instagram AI Selector
 * Turn the Instagram AI auto-reply ON/OFF for the workspace, and (when on) pin
 * one AI agent to answer ALL inbound Instagram DMs — the Instagram analogue of
 * WhatsAppAgentSelector, plus the explicit on/off gate Instagram needs (unlike
 * WhatsApp, Instagram defaults to human-handled, so the AI only replies once a
 * tenant opts in). Saved via /admin/instagram-agent-settings.
 *
 * When AI is OFF, Instagram behaves exactly as before: inbound DMs go to the
 * team inbox for manual reply, new threads auto-assign to a human. When ON, new
 * threads open in bot mode and the AI answers them (handing off to a human on
 * request). Only ACTIVE agents are offered; if the pinned agent later becomes
 * inactive the AI sends no Instagram reply until another agent is picked.
 */
import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Stack,
  TextField,
  MenuItem,
  CircularProgress,
  Switch,
  FormControlLabel,
  Box,
} from '@mui/material';
import { Check } from '@mui/icons-material';
import adminApi from '@/services/admin.api';

export default function InstagramAgentSelector() {
  // Active agents only — an inactive agent never answers, so it isn't offered.
  const [agents, setAgents] = useState<Array<{ _id: string; name: string }>>([]);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [agentId, setAgentId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    Promise.all([
      adminApi.getAgents({ limit: 100, isActive: true }),
      adminApi.getInstagramAgentSettings(),
    ])
      .then(([ag, cfg]) => {
        const list = (((ag as any).data ?? ag) as Array<{ _id: string; name: string }>) || [];
        setAgents(list.map((a) => ({ _id: a._id, name: a.name })));
        setAiEnabled(cfg.aiEnabled === true);
        setAgentId(cfg.defaultAgentId ?? '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // A pin that is not in the active list is inactive or deleted. The backend
  // sends no AI reply for it, so surface that instead of showing a blank select.
  const pinUnavailable = !loading && agentId !== '' && !agents.some((a) => a._id === agentId);

  const flash = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const saveEnabled = async (value: boolean) => {
    setAiEnabled(value);
    setSaving(true);
    try {
      await adminApi.updateInstagramAgentSettings({ aiEnabled: value });
      flash();
    } catch {
      /* non-fatal */
    } finally {
      setSaving(false);
    }
  };

  const saveAgent = async (value: string) => {
    setAgentId(value);
    setSaving(true);
    try {
      await adminApi.updateInstagramAgentSettings({ agentId: value || null });
      flash();
    } catch {
      /* non-fatal */
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary" gutterBottom display="block">
          AI agent — answers Instagram DMs
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={aiEnabled}
              onChange={(e) => saveEnabled(e.target.checked)}
              disabled={loading || saving}
            />
          }
          label="Let AI auto-reply to Instagram DMs"
        />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          {aiEnabled
            ? 'New Instagram conversations are answered by AI (within the 24-hour window), with handoff to a human on request. Turn off to keep Instagram manual-reply only.'
            : 'Off — Instagram DMs go to your team inbox for manual reply, exactly as before. Turn on to let AI answer them.'}
        </Typography>

        <Box sx={{ opacity: aiEnabled ? 1 : 0.5, pointerEvents: aiEnabled ? 'auto' : 'none' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Instagram agent"
              value={pinUnavailable ? '' : agentId}
              onChange={(e) => saveAgent(e.target.value)}
              disabled={loading || saving || !aiEnabled}
              error={pinUnavailable}
              helperText={
                loading
                  ? 'Loading…'
                  : pinUnavailable
                    ? 'The pinned agent is inactive or deleted, so Instagram AI is not replying. Pick an active agent.'
                    : agents.length === 0
                      ? 'No active AI agents — activate or create one under Agents.'
                      : agentId
                        ? 'This agent answers every Instagram DM (keyword routing bypassed).'
                        : 'Automatic — the best-matching active agent is chosen per message.'
              }
            >
              <MenuItem value="">
                <em>Automatic (keyword routing)</em>
              </MenuItem>
              {agents.map((a) => (
                <MenuItem key={a._id} value={a._id}>
                  {a.name}
                </MenuItem>
              ))}
            </TextField>
            {saving ? (
              <CircularProgress size={20} />
            ) : saved ? (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'success.main', flexShrink: 0 }}>
                <Check sx={{ fontSize: 16 }} />
                <Typography variant="caption" fontWeight={600}>
                  Saved
                </Typography>
              </Stack>
            ) : null}
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}
